import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { FiBell, FiCalendar, FiChevronDown, FiSearch, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import WalkInAlertsPill from "../../components/WalkInAlertsPill";

interface APITransaction {
  transactionId: string;
  orderId: string;
  customerName: string;
  transactionTime: string;
  agentAssigned: string;
  paymentMethod: string;
  transactionAmount: number;
}

export default function CashFlow() {
  const [allTransactions, setAllTransactions] = useState<APITransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("this_month");
  
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  const totalPages = Math.ceil(allTransactions.length / itemsPerPage) || 1;
  const currentTransactions = allTransactions.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const [totals, setTotals] = useState({ totalIncome: 0, cash: 0, card: 0, bankTransfer: 0 });
  const [totalsLoading, setTotalsLoading] = useState(true);

  // Handler for options menu on cards
  const [showOptions, setShowOptions] = useState(false);

  const getToken = () => {
    const auth = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    return jsonData?.token as string | undefined;
  };

  const authHeaders = (token?: string) =>
    token ? { Authorization: `Bearer ${token}` } : undefined;

  const fetchTotals = useCallback(async () => {
    setTotalsLoading(true);
    try {
      const token = getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/front-desk/admin/cash-flow?filter=${dateFilter}`,
        { headers: authHeaders(token) }
      );
      if (res.data?.success) {
        setTotals(res.data.data.totals);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to fetch totals");
    } finally {
      setTotalsLoading(false);
    }
  }, [dateFilter]);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      let url = `${process.env.REACT_APP_API_URL}/front-desk/admin/cash-flow/transactions?limit=100000`;
      if (searchQuery) url += `&q=${searchQuery}`;
      if (dateFilter) url += `&filter=${dateFilter}`;
      if (paymentMethodFilter && paymentMethodFilter !== "all") url += `&paymentMethod=${paymentMethodFilter}`;
      
      const res = await axios.get(url, { headers: authHeaders(token) });
      if (res.data?.success) {
        setAllTransactions(res.data.data.data || []);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to fetch transactions");
    } finally {
      setLoading(false);
    }
  }, [searchQuery, dateFilter, paymentMethodFilter]);

  useEffect(() => {
    fetchTotals();
  }, [fetchTotals]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchTransactions();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [fetchTransactions]);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", minimumFractionDigits: 2 }).format(amount);

  const getPaymentMethodDisplay = (method?: string) => {
    if (!method) return "-";
    const lower = method.toLowerCase();
    if (lower.includes(",") || lower.includes(" and ") || lower === "multiple") return "Multiple";
    return method;
  };

  const getPaymentMethodStyle = (method?: string) => {
    const display = getPaymentMethodDisplay(method).toLowerCase();
    if (display === "multiple") return "bg-purple-50 text-purple-700";
    if (display === "cash") return "bg-[#EAF9F2] text-[#008060]";
    if (display === "card" || display === "pos") return "bg-[#FFF6E9] text-amber-600";
    return "bg-[#EAF2FD] text-blue-600";
  };

  return (
    <div className="w-full min-h-screen p-4 xl:p-6 flex flex-col gap-6 font-sans">
      {/* Header section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-[22px] font-bold text-gray-950">Cashflow Report</h1>
          <p className="text-gray-400 text-sm mt-0.5 capitalize">Revenue breakdown for {dateFilter.replace('_', ' ')}</p>
        </div>

        {/* Top-right Controls */}
        <div className="flex items-center gap-3 self-stretch md:self-auto flex-wrap">
          {/* Alerts Pill */}
          <WalkInAlertsPill />

          {/* Date Picker Pill */}
          <button className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-gray-100 shadow-sm text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors">
            <FiCalendar className="w-4 h-4 text-[#008060]" />
            <span>{new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}, {new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}</span>
          </button>

          {/* Export Pill */}
          <button className="flex items-center justify-between gap-3 px-4 py-2 bg-[#008060] text-white rounded-lg text-sm font-semibold hover:bg-[#006b50] transition-colors shadow-sm min-w-[150px]">
            <span>Export Summary</span>
            <FiChevronDown className="text-white" />
          </button>
        </div>
      </div>

      {/* Grid of Cards (4 cards in a row) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {/* Card 1: Total Revenue */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100/50 flex flex-col justify-between min-h-[125px] relative">
          <div className="flex justify-between items-start">
            <p className="text-gray-400 text-xs font-semibold tracking-wide">Total Revenue</p>
            <button 
              onClick={() => setShowOptions(!showOptions)}
              className="text-gray-400 hover:text-gray-600 font-bold text-lg leading-none"
            >
              ⋮
            </button>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <h3 className="text-[26px] font-black text-gray-900 leading-none">
              {totalsLoading ? <span className="inline-block w-24 h-7 bg-gray-100 rounded animate-pulse" /> : formatCurrency(totals.totalIncome)}
            </h3>
            <span className="text-[#4ade80] text-base font-bold select-none">↑</span>
          </div>
          <p className="text-gray-400 text-[11px] font-medium mt-2 capitalize">{dateFilter.replace('_', ' ')} Total Revenue</p>
        </div>

        {/* Card 2: Cash */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100/50 flex flex-col justify-between min-h-[125px]">
          <p className="text-gray-400 text-xs font-semibold tracking-wide">Cash</p>
          <div className="flex items-center gap-2 mt-2">
            <h3 className="text-[26px] font-black text-gray-900 leading-none">
              {totalsLoading ? <span className="inline-block w-24 h-7 bg-gray-100 rounded animate-pulse" /> : formatCurrency(totals.cash)}
            </h3>
            <span className="text-[#4ade80] text-base font-bold select-none">↑</span>
          </div>
          <p className="text-gray-400 text-[11px] font-medium mt-2">Cash Transactions</p>
        </div>

        {/* Card 3: Card */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100/50 flex flex-col justify-between min-h-[125px]">
          <p className="text-gray-400 text-xs font-semibold tracking-wide">Card</p>
          <h3 className="text-[26px] font-black text-gray-900 mt-2 leading-none">
            {totalsLoading ? <span className="inline-block w-24 h-7 bg-gray-100 rounded animate-pulse" /> : formatCurrency(totals.card)}
          </h3>
          <p className="text-gray-400 text-[11px] font-medium mt-2">Card Transactions</p>
        </div>

        {/* Card 4: Transfers */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100/50 flex flex-col justify-between min-h-[125px]">
          <p className="text-gray-400 text-xs font-semibold tracking-wide">Transfers</p>
          <h3 className="text-[26px] font-black text-gray-900 mt-2 leading-none">
            {totalsLoading ? <span className="inline-block w-24 h-7 bg-gray-100 rounded animate-pulse" /> : formatCurrency(totals.bankTransfer)}
          </h3>
          <p className="text-gray-400 text-[11px] font-medium mt-2">Transfer Transactions</p>
        </div>
      </div>

      {/* Filter and Table Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-50 p-5 flex flex-col gap-6 mt-2">
        {/* Controls row */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <h2 className="text-base font-bold text-gray-900 whitespace-nowrap">Recent Transactions</h2>
          
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
            {/* Search Input */}
            <div className="relative flex-1 sm:w-[320px] lg:w-[350px]">
              <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by Customer name or Agent"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 bg-gray-50/50 border border-gray-100 rounded-lg text-sm text-gray-800 focus:outline-none focus:border-[#008060] focus:bg-white transition-all placeholder-gray-400"
              />
            </div>

            {/* Filter Select Dropdown */}
            <div className="relative min-w-[140px]">
              <select
                value={dateFilter}
                onChange={(e) => {
                  setDateFilter(e.target.value);
                  setPage(1);
                }}
                className="w-full appearance-none pr-8 pl-3 py-2 bg-gray-50 border border-gray-100 rounded-lg text-sm text-gray-700 font-semibold focus:outline-none cursor-pointer focus:border-[#008060]"
              >
                <option value="today">Today</option>
                <option value="this_week">This Week</option>
                <option value="last_week">Last Week</option>
                <option value="this_month">This Month</option>
                <option value="this_year">This Year</option>
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                <FiChevronDown />
              </div>
            </div>

            {/* Payment Method Dropdown */}
            <div className="relative min-w-[150px]">
              <select
                value={paymentMethodFilter}
                onChange={(e) => {
                  setPaymentMethodFilter(e.target.value);
                  setPage(1);
                }}
                className="w-full appearance-none pr-8 pl-3 py-2 bg-gray-50 border border-gray-100 rounded-lg text-sm text-gray-700 font-semibold focus:outline-none cursor-pointer focus:border-[#008060]"
              >
                <option value="all">All Methods</option>
                <option value="cash">Cash</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="pos">POS</option>
                <option value="multiple">Multiple</option>
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                <FiChevronDown />
              </div>
            </div>
          </div>
        </div>

        {/* Transactions Table */}
        <div className="overflow-x-auto min-h-[300px] flex flex-col">
          {loading ? (
            <div className="flex justify-center items-center flex-1">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#008060]" />
            </div>
          ) : (
            <>
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="bg-[#EAF5EF] text-[#008060] text-xs font-bold rounded-lg">
                    <th className="p-3.5 pl-4 rounded-l-lg font-bold tracking-wide">Transaction ID</th>
                    <th className="p-3.5 font-bold tracking-wide">Customer Name</th>
                    <th className="p-3.5 font-bold tracking-wide">Date of Transaction</th>
                    <th className="p-3.5 font-bold tracking-wide">Agent Assigned</th>
                    <th className="p-3.5 text-center font-bold tracking-wide">Payment Method</th>
                    <th className="p-3.5 pr-4 text-right rounded-r-lg font-bold tracking-wide">Transaction Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50/50 text-gray-700 font-medium">
                  {currentTransactions.length > 0 ? (
                    currentTransactions.map((tx) => (
                      <tr key={tx.transactionId} className="hover:bg-gray-50/30 transition-colors">
                        <td className="p-3.5 pl-4 text-gray-500 font-normal">{tx.transactionId}</td>
                        <td className="p-3.5 text-gray-900 font-semibold">{tx.customerName || "-"}</td>
                        <td className="p-3.5 text-gray-500">{new Date(tx.transactionTime).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</td>
                        <td className="p-3.5 text-gray-900">{tx.agentAssigned || "-"}</td>
                        <td className="p-3.5 text-center">
                          <span className={`inline-block px-4 py-1 rounded-md text-xs font-semibold capitalize ${getPaymentMethodStyle(tx.paymentMethod)}`}>
                            {getPaymentMethodDisplay(tx.paymentMethod)}
                          </span>
                        </td>
                        <td className="p-3.5 pr-4 text-right text-gray-900 font-bold">
                          {formatCurrency(tx.transactionAmount)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center py-10 text-gray-400">
                        No transactions found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              
              {/* Pagination controls */}
              {allTransactions.length > 0 && (
                <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-50">
                  <span className="text-sm text-gray-500">
                    Page {page} of {totalPages}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="p-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FiChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="p-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FiChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
